import type { ToastOptions } from './types'

import { useUiStore } from '@/shared/ui/store/uiStore'
import type { UiTone } from '@/shared/ui/types'

// Imperative toast service. sweetalert2 is NOT used (ledger C flags XSS/hardcoded-copy issues);
// this is a clean token-based queue rendered by <ToastHost/>.
export function useToast() {
  const store = useUiStore()

  function show(options: ToastOptions): number {
    const progress = options.progress ?? false
    return store.pushToast({
      message: options.message,
      tone: options.tone ?? 'neutral',
      timeout: progress ? 0 : (options.timeout ?? 4000),
      progress,
      placement: options.placement ?? 'top-end',
      dismissible: options.dismissible ?? true,
      ...(options.title ? { title: options.title } : {}),
      ...(options.action ? { action: options.action } : {}),
    })
  }

  function update(id: number, patch: Partial<ToastOptions>): void {
    store.updateToast(id, {
      ...patch,
      ...(patch.progress ? { timeout: 0 } : {}),
    })
  }

  function dismiss(id: number): void {
    store.dismissToast(id)
  }

  const withTone =
    (tone: UiTone) =>
    (message: string, options: Omit<ToastOptions, 'message' | 'tone'> = {}): number =>
      show({ message, tone, ...options })

  function loader(
    message: string,
    options: Omit<ToastOptions, 'message' | 'progress' | 'placement' | 'dismissible'> = {},
  ): number {
    return show({
      message,
      tone: 'info',
      ...options,
      progress: true,
      placement: 'bottom-end',
      dismissible: false,
    })
  }

  return {
    show,
    success: withTone('success'),
    error: withTone('danger'),
    warning: withTone('warning'),
    info: withTone('info'),
    loader,
    update,
    dismiss,
  }
}
